import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Item = { id: number; tipo: string; codigo: string; nombre: string; metadatos: any; activo: boolean };

export function Catalogos() {
  const { tienePermiso } = useAuth();
  const puedeEditar = tienePermiso("core.catalogos:editar");

  const [items, setItems] = useState<Item[]>([]);
  const [tipos, setTipos] = useState<string[]>([]);
  const [tipoActivo, setTipoActivo] = useState<string>("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [tipo, setTipo] = useState("");
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [i, t] = await Promise.all([api.get("/catalogos"), api.get("/catalogos/tipos")]);
      setItems(i);
      setTipos(t);
      if (!tipoActivo && t.length) setTipoActivo(t[0]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar los catálogos.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    setTipo(tipoActivo || ""); setCodigo(""); setNombre("");
    setFormError(null);
    setModalAbierto(true);
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/catalogos", { tipo, codigo, nombre });
      setModalAbierto(false);
      await cargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo crear el ítem.");
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (item: Item) => {
    try {
      await api.put(`/catalogos/${item.id}`, { activo: !item.activo });
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar el ítem.");
    }
  };

  const eliminar = async (item: Item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}" del catálogo ${item.tipo}?`)) return;
    try {
      await api.del(`/catalogos/${item.id}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el ítem.");
    }
  };

  const visibles = items.filter((i) => !tipoActivo || i.tipo === tipoActivo);

  return (
    <Shell titulo="Catálogos" breadcrumb="Core / Catálogos">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-textmuted">Listas maestras compartidas por los módulos operativos (especies, unidades, bodegas, etc.).</p>
        {puedeEditar && <button className="btn-primary" onClick={abrirNuevo}>+ Nuevo ítem</button>}
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      {tipos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {tipos.map((t) => (
            <button
              key={t}
              onClick={() => setTipoActivo(t)}
              className={`badge border ${tipoActivo === t ? "bg-navy text-white border-navy" : "bg-white border-borderc text-slate-600"}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="card overflow-hidden">
        {cargando ? (
          <p className="p-6 text-sm text-textmuted">Cargando...</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-borderc">
                <th className="table-th">Código</th>
                <th className="table-th">Nombre</th>
                <th className="table-th">Estado</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((i) => (
                <tr key={i.id} className="border-b border-borderc last:border-0">
                  <td className="table-td font-mono text-xs font-semibold text-slate-800">{i.codigo}</td>
                  <td className="table-td">{i.nombre}</td>
                  <td className="table-td">
                    <span className={`badge ${i.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                      {i.activo ? "activo" : "inactivo"}
                    </span>
                  </td>
                  <td className="table-td text-right space-x-2">
                    {puedeEditar && (
                      <>
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => toggleActivo(i)}>
                          {i.activo ? "Desactivar" : "Activar"}
                        </button>
                        <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => eliminar(i)}>Eliminar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr><td colSpan={4} className="table-td text-center text-textmuted py-8">Sin ítems en este catálogo.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo ítem de catálogo</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de catálogo</label>
              <input className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)} placeholder="ej. especies, unidades, bodegas" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
              <input className="input-field font-mono" value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}
